import React, { useEffect, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MoreMenuButton } from '@modernloop/shared/components';
import { ErrorIcon, SendIcon } from '@modernloop/shared/icons';
import { Button, CircularProgress, MenuItem, Paper, PaperProps, Stack, Theme, Typography } from '@mui/material';

import {
  Schedule_ApplicationFragment,
  SelfScheduleOptionsModal_SelfScheduleFragment,
  SelfSchedule_JobStageSettingsFragment,
  useEmployeeByIdsQuery,
} from 'src/generated/mloop-graphql';

import ShareDialog from 'src/components/Dialogs/ShareDialog';
import { useDurationLabel } from 'src/components/DurationLabel';

import useIsApplicationRejected from 'src/hooks/useIsApplicationRejected';

import { useCandidatePortalUrl } from 'src/urls/hooks/useCandidatePortalUrl';

import RejectedScheduleAlert from '../ScheduleTask/ScheduleTaskRequirements/Schedule/RejectedScheduleAlert';

import SelfScheduleDisableAlert from './SelfScheduleDisableAlert';
import { SelfScheduleOptionsModalPage } from './SelfScheduleOptionsModal';
import SelfScheduleOptionsModalWrapper from './SelfScheduleOptionsModalWrapper';

type Fragments = {
  jobStageSettings: SelfSchedule_JobStageSettingsFragment | undefined;
};

type Props = {
  applicationId: string;
  candidateId: string;
  jobStageId: string;
  customJobStageId?: string;
  // eslint-disable-next-line modernloop/component-with-fragments.cjs
  taskSelfSchedule: SelfScheduleOptionsModal_SelfScheduleFragment;
  showSelfScheduleDialogs?: boolean;
  onClose?: (emailSent?: boolean) => void;
  application?: Schedule_ApplicationFragment;
};

const SelfSchedule: FCWithFragments<Fragments, Props> = ({
  applicationId,
  candidateId,
  jobStageId,
  customJobStageId,
  taskSelfSchedule,
  showSelfScheduleDialogs = false,
  onClose,
  application,
  jobStageSettings,
}) => {
  const { t } = useTranslation();
  const isApplicationRejected = useIsApplicationRejected(application?.status);
  const [showShareAlert, setShowShareAlert] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showDisableSelfScheduleAlert, setShowDisableSelfScheduleAlert] = useState(false);

  const { candidatePortalUrl: selfScheduleUrl } = useCandidatePortalUrl(applicationId, {
    taskId: taskSelfSchedule?.taskId,
  });

  const { data: employeeData } = useEmployeeByIdsQuery({
    variables: { input: [taskSelfSchedule?.lastUpdatedByEmployeeId] },
    skip: !taskSelfSchedule?.lastUpdatedByEmployeeId,
  });

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    // showOnlyShareDialogs will be set to true when a new self schedule request is created without sending email.
    // So lets show the share dialog in that case.
    if (showSelfScheduleDialogs !== undefined) {
      setShowShareAlert(showSelfScheduleDialogs);
    }
  }, [showSelfScheduleDialogs]);

  const durationLabel = useDurationLabel(taskSelfSchedule?.createdAt);
  const emailSentAtDurationLabel = useDurationLabel(taskSelfSchedule?.selfScheduleEmailSentAt);

  const handleShowShareAlert = () => setShowShareAlert(true);
  const handleHideShareAlert = () => {
    setShowShareAlert(false);
    if (onClose) {
      onClose();
    }
  };
  const handleShowOptionsModal = () => {
    setShowOptionsModal(true);
    if (!showSelfScheduleDialogs) {
      handleHideShareAlert();
    }
  };
  const handleHideOptionsModal = (emailSent?: boolean) => {
    if (emailSent) {
      setShowOptionsModal(false);
    } else {
      handleShowShareAlert();
    }

    if (onClose) {
      onClose(emailSent);
    }
  };

  const handleDeleteSelfSchedule = async () => {
    setShowDisableSelfScheduleAlert(false);
  };

  let paperColor: PaperProps['color'];
  let icon = <></>;
  let content = <></>;
  let actions = <CircularProgress size={20} />;

  if (isApplicationRejected) {
    paperColor = 'error';
    icon = <ErrorIcon color="error" />;
    content = (
      <RejectedScheduleAlert
        selfScheduleOptions={{
          createdAt: taskSelfSchedule.createdAt,
          selfScheduleEmailSentAt: taskSelfSchedule.selfScheduleEmailSentAt,
          creatorName: employeeData?.employeeByIds?.length === 1 ? (employeeData?.employeeByIds[0].fullName ?? '') : '',
        }}
        heading={t('hiring-dashboard-schedule:schedule.selfSchedule.rejectedHeading')}
        actionsJSX={
          <Stack alignItems="center" direction="row" sx={{ flexWrap: 'wrap', gap: 1, paddingTop: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setShowDisableSelfScheduleAlert(true);
              }}
            >
              {t('hiring-dashboard-schedule:schedule.selfSchedule.cancelRequest')}
            </Button>
          </Stack>
        }
      />
    );
    actions = <></>;
  } else {
    paperColor = taskSelfSchedule.taskId ? 'pending' : 'contrast';
    content = (
      <Stack spacing={0.25} flexGrow={1}>
        <Typography color="text.secondary" fontWeight={600}>
          {t('hiring-dashboard-schedule:schedule.selfSchedule.waitingForCandidate')}
        </Typography>
        {employeeData?.employeeByIds?.length === 1 && (
          <>
            {taskSelfSchedule.selfScheduleEmailSentAt && (
              <Typography color="text.secondary" variant="body2">
                {t('hiring-dashboard-schedule:schedule.selfSchedule.linkSentBy', {
                  duration: emailSentAtDurationLabel,
                  name: employeeData?.employeeByIds[0].fullName ?? '',
                })}
              </Typography>
            )}
            {!taskSelfSchedule.selfScheduleEmailSentAt && (
              <Typography color="text.secondary" variant="body2">
                {t('hiring-dashboard-schedule:schedule.selfSchedule.createdBy', {
                  duration: durationLabel,
                  name: employeeData?.employeeByIds[0].fullName ?? '',
                })}
              </Typography>
            )}
          </>
        )}
      </Stack>
    );
    icon = <SendIcon color={taskSelfSchedule.taskId ? 'warning' : 'inherit'} />;
    actions = (
      <Stack direction="row" alignItems="center" flexShrink={1} spacing={1}>
        <Button data-testid="self-schedule-re-share-link-button" onClick={handleShowShareAlert}>
          {t('hiring-dashboard-schedule:schedule.selfSchedule.reShareLink')}
        </Button>
        <MoreMenuButton dataTestId="self-schedule-more-menu-button">
          <MenuItem id="self-schedule-option-delete" onClick={() => setShowDisableSelfScheduleAlert(true)}>
            <Typography color="error">{t('hiring-dashboard-schedule:schedule.selfSchedule.cancelRequest')}</Typography>
          </MenuItem>
        </MoreMenuButton>
      </Stack>
    );
  }

  const selfScheduleOptionsModalPage = taskSelfSchedule?.id
    ? SelfScheduleOptionsModalPage.SHARE_SELF_SCHEDULE
    : SelfScheduleOptionsModalPage.OPTIONS;

  return (
    <>
      <Paper
        variant="outlined"
        color={paperColor}
        sx={{
          borderRadius: 1.5,
          paddingTop: 2.5,
          paddingRight: 1.5,
          paddingBottom: 2.5,
          paddingLeft: 2,
          backgroundColor: (theme: Theme) => {
            if (paperColor === 'pending') {
              return theme.palette.warning.tint;
            }

            if (paperColor === 'contrast') {
              return theme.palette.background.contrast;
            }

            if (paperColor === 'error') {
              return theme.palette.error.tint;
            }

            return theme.palette.background.default;
          },
        }}
      >
        <Stack direction="row" spacing={1}>
          {icon}
          {content}
          {actions}
        </Stack>
      </Paper>
      {showShareAlert && taskSelfSchedule?.id && (
        <ShareDialog
          longUrl={selfScheduleUrl}
          content={t('hiring-dashboard-schedule:schedule.selfSchedule.shareContent')}
          onClose={handleHideShareAlert}
          onSendEmail={handleShowOptionsModal}
        />
      )}
      {showOptionsModal && (
        <SelfScheduleOptionsModalWrapper
          jobId={application?.job?.id}
          applicationId={applicationId}
          candidateId={candidateId}
          jobStageId={jobStageId}
          customJobStageId={customJobStageId}
          taskId={taskSelfSchedule?.taskId}
          // Only show the send later option if this is the initial create of the self schedule request and not a re-share
          showSendLater={
            !taskSelfSchedule && selfScheduleOptionsModalPage !== SelfScheduleOptionsModalPage.SHARE_SELF_SCHEDULE
          }
          selfSchedule={taskSelfSchedule || undefined}
          selfScheduleOptionsModalPage={selfScheduleOptionsModalPage}
          onClose={handleHideOptionsModal}
          jobStageSettingsV2={jobStageSettings}
          interviewMeetingLocation={
            jobStageSettings?.interviewMeetingLocationSettingWithLevel?.interviewMeetingLocationSetting || undefined
          }
        />
      )}
      {showDisableSelfScheduleAlert && taskSelfSchedule?.id && (
        <SelfScheduleDisableAlert
          selfScheduleOptionsId={taskSelfSchedule.id}
          onClose={() => setShowDisableSelfScheduleAlert(false)}
          onDelete={handleDeleteSelfSchedule}
        />
      )}
    </>
  );
};

SelfSchedule.fragments = {
  jobStageSettings: gql`
    ${SelfScheduleOptionsModalWrapper.fragments.interviewMeetingLocation}
    ${SelfScheduleOptionsModalWrapper.fragments.jobStageSettingsV2}
    fragment SelfSchedule_jobStageSettings on JobStageSettingsV2 {
      id
      ...SelfScheduleOptionsModalWrapper_jobStageSettingsV2
      interviewMeetingLocationSettingWithLevel {
        interviewMeetingLocationSetting {
          ...SelfScheduleOptionsModalWrapper_interviewMeetingLocation
        }
      }
    }
  `,
};

export default SelfSchedule;
